import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { COUNTRIES } from "@/i18n/translations";
import { PackageCards } from "@/components/PackageCards";
import { IdUploader } from "@/components/IdUploader";
import { PageLoader } from "@/components/ProtectedRoute";
import { useRegisterForm } from "@/hooks/useRegisterForm";

const Field = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-sm font-semibold">{label}</Label>{children}</div>
);

export default function Register() {
  const { t } = useLang();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const { form, setField, pkg, setPkg, photo, setPhoto, error, busy, submit } = useRegisterForm(t, params.get("package"));

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  if (!settings) return <PageLoader />;

  const set = (k) => (e) => setField(k, e.target.value);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:py-16">
      <h1 className="animate-fade-up text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="register-title">{t("reg_title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("reg_subtitle")}</p>
      {!settings.registration_open ? (
        <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-6 font-semibold text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-300" data-testid="register-closed-notice">
          {t("reg_closed")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-10" data-testid="register-form">
          <div className="grid gap-6 rounded-2xl border border-border bg-card p-6 sm:p-8 md:grid-cols-2">
            <Field label={t("full_name")}>
              <Input value={form.full_name} onChange={set("full_name")} placeholder={t("full_name_ph")} data-testid="register-full-name-input" className="h-11" />
            </Field>
            <Field label={t("phone")}>
              <Input type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder={t("phone_ph")} data-testid="register-phone-input" className="h-11" />
            </Field>
            <Field label={t("country")}>
              <Select value={form.country} onValueChange={(v) => setField("country", v)}>
                <SelectTrigger className="h-11" data-testid="register-country-select"><SelectValue placeholder={t("country_ph")} /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c} data-testid={`country-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="hidden md:block" />
            <Field label={t("password")}>
              <Input type="password" value={form.password} onChange={set("password")} placeholder={t("password_ph")} data-testid="register-password-input" className="h-11" />
            </Field>
            <Field label={t("confirm_password")}>
              <Input type="password" value={form.confirm} onChange={set("confirm")} data-testid="register-confirm-password-input" className="h-11" />
            </Field>
            <div className="md:col-span-2">
              <Field label={t("id_photo")}><IdUploader file={photo} onChange={setPhoto} /></Field>
            </div>
          </div>
          <div>
            <h2 className="mb-5 text-lg font-bold">{t("package")}</h2>
            <PackageCards packages={settings.packages} selected={pkg} onSelect={setPkg} />
          </div>
          {error && <p className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm font-semibold text-rose-800 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-300" data-testid="register-error">{error}</p>}
          <div className="flex flex-col-reverse items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">{t("have_account")} <Link to="/login" className="font-semibold text-primary" data-testid="register-login-link">{t("nav_login")}</Link></p>
            <button type="submit" disabled={busy} data-testid="register-submit-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 font-bold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60 sm:w-auto">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? t("submitting") : t("submit_register")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
